import React, { type CSSProperties } from "react";
import { useNavigate } from "react-router";

export type KpiTone = "neutral" | "ok" | "warn" | "crit" | "accent";

interface KpiTileProps {
  label: string;
  value: React.ReactNode;
  /** Small line under the value: breakdown or comparison */
  detail?: React.ReactNode;
  /** Optional delta shown next to the detail, e.g. "+6.1% vs Aug" */
  delta?: { text: string; tone: "ok" | "crit" | "neutral" };
  tone?: KpiTone;
  icon?: string;
  to?: string;
  isLoading?: boolean;
}

const TONE_COLORS: Record<KpiTone, string> = {
  neutral: "#111827",
  ok: "#059669",
  warn: "#d97706",
  crit: "#dc2626",
  accent: "#4f46e5",
};

const DELTA_COLORS = { ok: "#059669", crit: "#dc2626", neutral: "#6b7280" };

/**
 * Admin KPI tile: value + context + link to the list behind the number.
 */
const KpiTile: React.FC<KpiTileProps> = ({ label, value, detail, delta, tone = "neutral", icon, to, isLoading }) => {
  const navigate = useNavigate();
  const clickable = !!to;

  return (
    <div
      className="kpi-tile"
      style={{ ...styles.tile, ...(clickable ? styles.clickable : {}) }}
      onClick={clickable ? () => navigate(to) : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              // ARIA button semantics require Space as well as Enter.
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                navigate(to);
              }
            }
          : undefined
      }
    >
      <div style={styles.head}>
        <span style={styles.label}>{label}</span>
        {icon && <i className={icon} style={styles.icon} aria-hidden="true" />}
      </div>
      <div style={{ ...styles.value, color: TONE_COLORS[tone] }}>
        {isLoading ? <span style={styles.skeleton} /> : value}
      </div>
      {(detail || delta) && !isLoading && (
        <div style={styles.detail}>
          {delta && <span style={{ color: DELTA_COLORS[delta.tone], fontWeight: 600 }}>{delta.text}</span>}
          {delta && detail && <span style={styles.dot}>·</span>}
          {detail && <span>{detail}</span>}
        </div>
      )}
      {isLoading && <div style={{ ...styles.skeleton, width: "60%", height: 10, marginTop: 8 }} />}
    </div>
  );
};

const styles: Record<string, CSSProperties> = {
  tile: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "14px 16px",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    minWidth: 0,
    height: "100%",
    transition: "box-shadow 0.15s, transform 0.15s",
  },
  clickable: { cursor: "pointer" },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 },
  label: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "#6b7280",
    fontWeight: 600,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  icon: { color: "#9ca3af", fontSize: 13 },
  value: { fontSize: 26, fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1.15, fontVariantNumeric: "tabular-nums" },
  detail: { fontSize: 12, color: "#6b7280", display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" },
  dot: { color: "#d1d5db" },
  skeleton: {
    display: "inline-block",
    width: 90,
    height: 22,
    borderRadius: 6,
    background: "linear-gradient(90deg, #f3f4f6, #e5e7eb, #f3f4f6)",
  },
};

export default KpiTile;
